/** Spec §14.2 page 4 + §14.3: each control shows its real confirmed state after the API answers. */
import { useState } from "react";

import { useApps, useCommandStatus, useDeviceAction, useRequestDecision, useRequests } from "../api/hooks.js";
import { StateBadge } from "../components/StateBadge.js";
import { Empty, PageState } from "../components/PageState.js";

type Control = { action: string; label: string; needsPackage: boolean; minutes?: number };

const ACTIONS: Control[] = [
  { action: "stop-game", label: "Stop game", needsPackage: true },
  { action: "block-game", label: "Temporary block", needsPackage: true },
  { action: "unblock-game", label: "Unblock", needsPackage: true },
  { action: "manual-lock", label: "Manual lock", needsPackage: false },
  { action: "bonus-time", label: "+15 minutes", needsPackage: false, minutes: 15 },
  { action: "bonus-time", label: "+30 minutes", needsPackage: false, minutes: 30 },
];

export function LiveControlsPage({ deviceId }: { deviceId: string }) {
  const apps = useApps(deviceId);
  const requests = useRequests(deviceId);
  const action = useDeviceAction(deviceId);
  const decision = useRequestDecision();
  const [packageId, setPackageId] = useState("");
  const [commandId, setCommandId] = useState<string | null>(null);
  const status = useCommandStatus(deviceId, commandId);

  if (!deviceId) return <Empty label="No device selected." />;

  return (
    <PageState query={{ isPending: apps.isPending, error: apps.error }}>
      <section className="stack">
        <h3>Remote controls</h3>
        <label htmlFor="pkg">Target app</label>
        <select id="pkg" value={packageId} onChange={(e) => setPackageId(e.target.value)}>
          <option value="">— select —</option>
          {(apps.data ?? []).map((a) => (
            <option key={a.package_id} value={a.package_id}>{a.display_name ?? a.package_id}</option>
          ))}
        </select>
        <div className="row">
          {ACTIONS.map((ctl) => (
            <button
              key={ctl.label}
              disabled={action.isPending || (ctl.needsPackage && !packageId)}
              onClick={() =>
                action.mutate(
                  { action: ctl.action, body: { ...(ctl.needsPackage ? { package_id: packageId } : {}), ...(ctl.minutes ? { minutes: ctl.minutes } : {}) } },
                  { onSuccess: (r) => setCommandId(r.command_id) },
                )
              }
            >
              {ctl.label}
            </button>
          ))}
        </div>
        {action.error ? <p className="notice danger">{action.error.message}</p> : null}
        {commandId ? (
          <div className="notice">
            Command {commandId.slice(0, 8)}… reported state:{" "}
            {status.isPending ? (
              "checking…"
            ) : (
              <StateBadge state={status.data?.state ?? "REQUESTED"} />
            )}
            <button onClick={() => setCommandId(null)}>Dismiss</button>
          </div>
        ) : null}
        <p className="muted">
          A queued command is REQUESTED. It only becomes APPLIED/VERIFIED when the headset agent reports back
          with evidence, so this panel never claims enforcement that has not happened.
        </p>
      </section>
      <section className="stack">
        <h3>Pending child requests</h3>
        {(requests.data ?? []).length === 0 ? (
          <p className="notice">No pending requests.</p>
        ) : (
          (requests.data ?? []).map((r) => (
            <div key={r.id} className="row">
              <span>{r.requested_minutes} min bonus — “{r.message ?? "no message"}”</span>
              <button disabled={decision.isPending} onClick={() => decision.mutate({ requestId: r.id, approve: true })}>Approve</button>
              <button disabled={decision.isPending} onClick={() => decision.mutate({ requestId: r.id, approve: false })}>Deny</button>
            </div>
          ))
        )}
      </section>
    </PageState>
  );
}
